import { getLeaderboardXpGap } from "@memorang/helpers/leaderboard";
import { useState } from "react";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import Container from "../Container";
import { LeaderboardContent } from "./LeaderboardContent";
import { LeaderboardHeader } from "./LeaderboardHeader";
import { LeaderboardUserPositionView } from "./LeaderboardUserPositionView";
import type {
	LeaderboardAvatarType,
	LeaderboardConfig,
	LeaderboardFilterValue,
	LeaderboardItem,
	LeaderboardScope,
	LeaderboardTimePeriod,
} from "./types";

type Props = {
	items: LeaderboardItem[];
	currentUserItem: LeaderboardItem | undefined;
	timePeriod: LeaderboardTimePeriod;
	scope: LeaderboardScope;
	config: LeaderboardConfig;
	onFilterChange: (value: LeaderboardFilterValue) => void;
	avatarType?: LeaderboardAvatarType;
	isLoading?: boolean;
	error?: { title?: string; description?: string };
	onStart?: () => void;
	onRefresh?: () => void;
};

export const LeaderboardApplet = ({
	items,
	currentUserItem,
	timePeriod,
	scope,
	config,
	onFilterChange,
	avatarType,
	isLoading = false,
	error,
	onStart,
	onRefresh,
}: Props) => {
	const theme = useAppTheme();
	const [isScrolled, setIsScrolled] = useState(false);

	const xpGap = getLeaderboardXpGap(items, currentUserItem);
	const showFooter =
		currentUserItem !== undefined &&
		currentUserItem.rank > 0 &&
		scope === "global" &&
		items.length > 4;

	return (
		<Box flex={1}>
			<Box
				paddingHorizontal={16}
				paddingTop={16}
				paddingBottom={12}
				style={{
					zIndex: 10,
					// backgroundColor: theme.colors.background,
					...(isScrolled && {
						shadowColor: theme.colors.shadow,
						shadowOffset: { width: 0, height: 2 },
						shadowOpacity: 0.15,
						shadowRadius: 4,
						elevation: 4,
					}),
				}}
			>
				<Box
					style={{
						width: "100%",
						maxWidth: theme.breakpoints?.sm,
						marginHorizontal: "auto",
					}}
				>
					<LeaderboardHeader
						timePeriod={timePeriod}
						scope={scope}
						timePeriods={config.timePeriods}
						scopes={config.scopes}
						onChange={onFilterChange}
					/>
				</Box>
			</Box>
			<Box flex={1} paddingHorizontal={16}>
				<LeaderboardContent
					items={items}
					isLoading={isLoading}
					currentUserItem={currentUserItem}
					avatarType={avatarType}
					showUsersPodium={config.showUsersPodium}
					numUsersToDisplay={config.numUsersToDisplay}
					scope={scope}
					error={error}
					onStart={onStart}
					onRefresh={onRefresh}
					onScrolled={(y) => setIsScrolled(y > 0)}
				/>
			</Box>
			{showFooter ? (
				<Box
					paddingHorizontal={16}
					paddingTop={12}
					paddingBottom={12}
					borderTopWidth={1}
					borderTopColor={theme.colors.border}
					backgroundColor={theme.colors.background}
				>
					<Container autoSize>
						<LeaderboardUserPositionView
							{...currentUserItem}
							xpGap={xpGap}
							avatarType={avatarType}
							numUsersToDisplay={config.numUsersToDisplay}
						/>
					</Container>
				</Box>
			) : null}
		</Box>
	);
};
